/**
 * Describes whether a horizontally-scrollable container has hidden content to
 * the left and/or right of the current viewport.
 *
 * - `atStart: true`  → no hidden content to the left  → no left fade needed
 * - `atEnd: true`    → no hidden content to the right → no right fade needed
 */
export interface ScrollFadeState {
  atStart: boolean;
  atEnd: boolean;
}

/** Input snapshot — all values taken directly from the scrollable DOM element. */
export interface ScrollDimensions {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
}

/**
 * Pure function: decides which scroll-fade edges should be hidden based on
 * a snapshot of the container's scroll position.
 *
 * A 1-pixel epsilon absorbs sub-pixel rounding differences that browsers
 * introduce when device pixel ratio is not 1.0 or when fractional scroll
 * positions accumulate.
 *
 * @example
 * const { atStart, atEnd } = computeScrollFadeState({
 *   scrollLeft: el.scrollLeft,
 *   scrollWidth: el.scrollWidth,
 *   clientWidth: el.clientWidth,
 * });
 */
export function computeScrollFadeState(dims: ScrollDimensions): ScrollFadeState {
  const EPSILON = 1;

  const { scrollLeft, scrollWidth, clientWidth } = dims;
  const maxScroll = scrollWidth - clientWidth;

  const hasOverflow = maxScroll > EPSILON;

  if (!hasOverflow) {
    return { atStart: true, atEnd: true };
  }

  const atStart = scrollLeft <= EPSILON;
  const atEnd = scrollLeft >= maxScroll - EPSILON;

  return { atStart, atEnd };
}
